/* eslint-disable react-refresh/only-export-components */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { AddIncomingStep2 } from '@/app/(client)/transactions/-components/AddIncomingStep2';
import { AddOutgoingStep1 } from '@/app/(client)/transactions/-components/AddOutgoingStep1';
import { AddOutgoingStep3 } from '@/app/(client)/transactions/-components/AddOutgoingStep3';
import { useAddOutgoingTransaction } from '@/app/(client)/transactions/-hooks/useAddOutgoingTransaction';
import { CreateCompanyDialog } from '@/components/shared/CreateCompanyDialog';
import { Stepper } from '@/components/shared/Stepper';
import { Button } from '@/components/ui/button';
import { useTranslator } from '@/hooks/useTranslator';

export const Route = createFileRoute(
  '/(client)/transactions/(pages)/(create)/incoming/create/',
)({
  component: CreateIncomingTransactionPage,
});

function CreateIncomingTransactionPage() {
  const { getTranslation: t } = useTranslator({ page: 'fiberTransactions' });
  const navigate = useNavigate();

  const ctrl = useAddOutgoingTransaction(
    () => void navigate({ to: '/transactions/incoming' }),
    {
      transactionType: 'INCOMING',
      invalidateQueryKey: 'fiber-incoming-transactions',
    },
  );

  const s = ctrl.form.currentStep;
  const steps = [
    {
      key: 'goods',
      label: t('create.step.goods'),
      status: s > 0 ? ('complete' as const) : ('in-progress' as const),
    },
    {
      key: 'info',
      label: t('create.step.info'),
      status:
        s > 1
          ? ('complete' as const)
          : s === 1
            ? ('in-progress' as const)
            : ('pending' as const),
    },
    {
      key: 'docs',
      label: t('create.step.docs'),
      status: s === 2 ? ('in-progress' as const) : ('pending' as const),
    },
  ];

  return (
    <>
      <div className='flex flex-col h-full bg-card'>
        <header className='sticky top-0 z-10 bg-card border-b border-border shrink-0'>
          <div className='max-w-6xl mx-auto px-6 py-5'>
            <Stepper steps={steps} currentStep={ctrl.form.currentStep + 1}>
              <div />
            </Stepper>
          </div>
        </header>

        <main className='flex-1 overflow-y-auto'>
          <div className='max-w-6xl mx-auto px-6 py-6 space-y-5'>
            <p className='text-sm font-semibold'>
              {s + 1}. {steps[s]?.label}
            </p>
            {s === 0 && <AddOutgoingStep1 ctrl={ctrl} />}
            {s === 1 && <AddIncomingStep2 ctrl={ctrl} />}
            {s === 2 && <AddOutgoingStep3 ctrl={ctrl} />}
          </div>
        </main>

        <footer className='sticky bottom-0 z-10 bg-card border-t border-border shrink-0'>
          <div className='max-w-6xl mx-auto px-6 py-4 flex items-center justify-between'>
            <Button
              type='button'
              variant='outline'
              onClick={() => void navigate({ to: '/transactions/incoming' })}
              disabled={ctrl.isSubmitting}
            >
              {t('create.cancel')}
            </Button>
            <div className='flex items-center gap-2'>
              {ctrl.form.currentStep > 0 && (
                <Button
                  type='button'
                  variant='outline'
                  onClick={ctrl.goBack}
                  disabled={ctrl.isSubmitting}
                >
                  <ChevronLeft className='size-4' />
                  {t('create.back')}
                </Button>
              )}
              {ctrl.form.currentStep < 2 ? (
                <Button
                  type='button'
                  onClick={ctrl.goNext}
                  disabled={!ctrl.isCurrentStepValid}
                >
                  {t('create.next')}
                  <ChevronRight className='size-4' />
                </Button>
              ) : (
                <Button
                  type='button'
                  onClick={() => void ctrl.submit()}
                  disabled={ctrl.isSubmitting || !ctrl.isCurrentStepValid}
                >
                  {ctrl.isSubmitting && (
                    <Loader2 className='size-4 animate-spin' />
                  )}
                  {t('create.submit')}
                </Button>
              )}
            </div>
          </div>
        </footer>
      </div>

      <CreateCompanyDialog
        open={ctrl.createCompanyDialog.open}
        onOpenChange={v => !v && ctrl.setCreateCompanyDialog({ open: false })}
        onCreated={ctrl.handleCompanyCreated}
      />
    </>
  );
}
